import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Paintbrush, Camera, Sparkles, Palette } from 'lucide-react';
import Bongo from '../components/Bongo';
import BrandSelector from '../components/BrandSelector';
import { useAuth } from '../hooks/useAuth';
import {
  ARTIST_NAME_MAX_LENGTH,
  countArtistNameLength,
  isArtistNameValid,
  normalizeArtistName,
  saveArtistName,
} from '../services/artistName';

// 4 steps when signed in (artistName included), 3 steps as guest:
// [0] langue + niveau, [1] artistName (if signed in), [2] marques, [3] premier scan
// Guests skip step 1 because artist_name needs a user.id to persist.
const STEP_SETUP = 'setup';
const STEP_ARTIST = 'artistName';
const STEP_BRANDS = 'brands';
const STEP_FIRST_SCAN = 'firstScan';

const SKILL_OPTIONS = [
  {
    id: 'beginner',
    labelFr: 'Débutant',
    labelEn: 'Beginner',
    descFr: 'Je commence tout juste',
    descEn: 'Just starting out',
  },
  {
    id: 'intermediate',
    labelFr: 'Intermédiaire',
    labelEn: 'Intermediate',
    descFr: 'Je pratique regulierement',
    descEn: 'I practice regularly',
  },
  {
    id: 'advanced',
    labelFr: 'Avance',
    labelEn: 'Advanced',
    descFr: 'Je maitrise les techniques',
    descEn: 'I know my techniques',
  },
];

export default function Onboarding({ t, language, setSelectedBrands, setSkillLevel, completeOnboarding }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id || null;
  const [step, setStep] = useState(0);
  const [selectedBrandIds, setSelectedBrandIds] = useState([]);
  const [skill, setSkill] = useState('intermediate');
  const [showNoColoringMessage, setShowNoColoringMessage] = useState(false);
  const [artistName, setArtistName] = useState('');
  const [artistSaving, setArtistSaving] = useState(false);
  const [artistError, setArtistError] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [artistSaved, setArtistSaved] = useState(false);

  const stepDefinitions = userId
    ? [STEP_SETUP, STEP_ARTIST, STEP_BRANDS, STEP_FIRST_SCAN]
    : [STEP_SETUP, STEP_BRANDS, STEP_FIRST_SCAN];
  const currentStepKey = stepDefinitions[step];

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.info('[WELCOME_STATE] onboarding_step', {
      step,
      language,
      selectedBrandsCount: selectedBrandIds.length,
      skill,
      timestamp: new Date().toISOString(),
    });
  }, [step, language, selectedBrandIds.length, skill]);

  const handleArtistContinue = async () => {
    if (!userId) {
      // Guest path: should never reach this step, but be defensive.
      setStep((s) => s + 1);
      return;
    }
    const trimmed = normalizeArtistName(artistName);
    if (!isArtistNameValid(trimmed)) {
      setArtistError(language === 'fr'
        ? `Choisis entre 1 et ${ARTIST_NAME_MAX_LENGTH} caracteres.`
        : `Pick between 1 and ${ARTIST_NAME_MAX_LENGTH} characters.`);
      return;
    }
    setArtistSaving(true);
    setArtistError('');
    const result = await saveArtistName(userId, trimmed);
    setArtistSaving(false);
    if (!result.ok) {
      if (result.error === 'name_taken') {
        setArtistError(language === 'fr'
          ? "Ce nom est déjà pris. Essaie une autre version (ajoute un emoji, un chiffre, etc.)."
          : 'This name is already taken. Try another version (add an emoji, a number, etc.).');
        return;
      }
      setArtistError(language === 'fr'
        ? "Sauvegarde impossible pour le moment. Réessaie."
        : 'Save failed. Please try again.');
      return;
    }
    setArtistSaved(true);
    setStep((s) => s + 1);
  };

  const handleFinish = () => {
    setSelectedBrands(selectedBrandIds);
    setSkillLevel(skill);
    completeOnboarding();
  };

  const handleGoToScan = () => {
    setSelectedBrands(selectedBrandIds);
    setSkillLevel(skill);
    completeOnboarding();
    navigate('/scan', { state: { fromOnboarding: true } });
  };

  const handleExploreWithoutScan = () => {
    setShowNoColoringMessage(true);
  };

  const canProceed =
    (currentStepKey === STEP_SETUP) ||
    (currentStepKey === STEP_ARTIST && isArtistNameValid(normalizeArtistName(artistName)) && !artistSaving) ||
    (currentStepKey === STEP_BRANDS && selectedBrandIds.length > 0) ||
    currentStepKey === STEP_FIRST_SCAN;

  return (
    <div className="cb-min-h-screen bg-[color:var(--cb-bg)] flex flex-col gradient-peach-wash">
      {/* Progress dots */}
      <div className="flex justify-center gap-2 pt-8 pb-4">
        {stepDefinitions.map((_, i) => (
          <div
            key={i}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === step ? 'w-8 bg-[color:var(--cb-primary)]' : 'w-2 bg-[rgba(61,61,61,0.15)]'
            }`}
          />
        ))}
      </div>

      <div className="flex-1 flex flex-col px-6 max-w-md mx-auto w-full">
        <AnimatePresence mode="wait">

          {/* ETAPE 0 : Reglages rapides (niveau seul, la langue est choisie en amont) */}
          {currentStepKey === STEP_SETUP && (
            <motion.div
              key="setup"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="flex-1 flex flex-col pt-6"
            >
              <h1 className="text-2xl font-extrabold text-[color:var(--cb-text)] mb-1">
                {language === 'fr' ? 'Quelques réglages rapides' : 'A few quick settings'}
              </h1>
              <p className="text-sm text-[color:var(--cb-text-muted)] mb-6">
                {language === 'fr'
                  ? 'Pour personnaliser ton parcours ColoBongo.'
                  : 'To personalize your ColoBongo journey.'}
              </p>

              {/* Niveau */}
              <h2 className="text-lg font-extrabold text-[color:var(--cb-text)] mb-1">
                {language === 'fr'
                  ? 'Ton niveau en coloriage ?'
                  : 'Your coloring level?'}
              </h2>
              <p className="text-sm text-[color:var(--cb-text-muted)] mb-4">
                {language === 'fr'
                  ? 'Choisis ce qui te ressemble le mieux pour demarrer en douceur.'
                  : 'Pick what fits you best so we can start gently.'}
              </p>
              <div className="flex flex-col gap-2.5">
                {SKILL_OPTIONS.map(option => (
                  <motion.button
                    key={option.id}
                    onClick={() => setSkill(option.id)}
                    className={`py-3 px-5 rounded-[18px] border-2 text-left transition-colors ${
                      skill === option.id
                        ? 'bg-[color:var(--cb-accent-2)] text-[color:var(--cb-text)] border-[color:var(--cb-accent-2)]'
                        : 'bg-white text-[color:var(--cb-text)] border-[rgba(61,61,61,0.12)]'
                    }`}
                    whileTap={{ scale: 0.97 }}
                  >
                    <span className="font-semibold block text-sm">
                      {language === 'fr' ? option.labelFr : option.labelEn}
                    </span>
                    <span className="text-xs text-[color:var(--cb-text-muted)] mt-0.5 block">
                      {language === 'fr' ? option.descFr : option.descEn}
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Étape artiste : nom permanent (signed-in only) */}
          {currentStepKey === STEP_ARTIST && (
            <motion.div
              key="artistName"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="flex-1 flex flex-col pt-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <Palette className="text-[color:var(--cb-primary)]" size={26} />
                <h2 className="text-2xl font-extrabold text-[color:var(--cb-text)]">
                  {language === 'fr' ? "Choisis ton nom d'artiste" : 'Pick your artist name'}
                </h2>
              </div>
              <p className="text-sm text-[color:var(--cb-text-muted)] mb-2 leading-relaxed">
                {language === 'fr'
                  ? "C'est le nom qui apparaitra sur tes coloriages au Mur. Tu pourras le modifier plus tard depuis ton profil."
                  : 'This is the name that will appear on your colorings on Le Mur. You can change it later from your profile.'}
              </p>
              <p className="text-xs text-[color:var(--cb-text-muted)] mb-5 leading-relaxed">
                {language === 'fr'
                  ? "Conseil : un petit emoji rend ton nom plus reconnaissable :)"
                  : 'Tip: a little emoji makes your name more recognizable :)'}
              </p>

              <input
                type="text"
                value={artistName}
                maxLength={ARTIST_NAME_MAX_LENGTH * 2}
                onChange={(e) => {
                  setArtistName(e.target.value);
                  if (artistError) setArtistError('');
                }}
                placeholder={language === 'fr' ? 'Ex: Emma 🎨' : 'Ex: Emma 🎨'}
                className="w-full px-4 py-3 rounded-2xl bg-white border border-[rgba(61,61,61,0.12)] text-base text-[color:var(--cb-text)] focus:outline-none focus:border-[color:var(--cb-primary)]"
                data-testid="onboarding-artist-name-input"
              />
              <div className="mt-1 text-[11px] text-[color:var(--cb-text-muted)] text-right">
                {countArtistNameLength(normalizeArtistName(artistName))} / {ARTIST_NAME_MAX_LENGTH}
              </div>

              {artistError && (
                <p className="mt-2 text-xs text-red-500">{artistError}</p>
              )}
            </motion.div>
          )}

          {/* â”€â”€ Ã‰TAPE 2 : Premier scan â”€â”€ */}
          {currentStepKey === STEP_FIRST_SCAN && (
            <motion.div
              key="firstScan"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="flex-1 flex flex-col pt-4"
            >
              <div className="flex justify-center mb-4">
                <Bongo mood="happy" size="large" idle />
              </div>

              <p className="cozy-section-kicker mb-1 text-center">
                {language === 'fr' ? 'Le cœur de Colobongo' : 'The heart of Colobongo'}
              </p>
              <h2 className="text-2xl font-extrabold text-[color:var(--cb-text)] mb-3 text-center">
                {language === 'fr' ? 'Ton premier scan !' : 'Your first scan!'}
              </h2>

              <div className="cozy-card p-4 mb-4 space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-xl leading-none">*</span>
                  <p className="text-sm text-[color:var(--cb-text-muted)] leading-relaxed">
                    {language === 'fr'
                      ? 'Prends en photo un coloriage que tu as déjà fait, meme ancien, meme imparfait. Bongo te donnera un retour chaleureux et des pistes pour progresser.'
                      : 'Take a photo of a coloring you\'ve already done, even old, even imperfect. Bongo will give you warm feedback and tips to grow.'}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-xl leading-none">*</span>
                  <p className="text-sm text-[color:var(--cb-text-muted)] leading-relaxed">
                    {language === 'fr'
                      ? 'Et si ton coloriage est pret a briller, tu pourras le partager sur Le Mur, notre galerie Colomunautaire. Tes créations pourraient inspirer d\'autres coloristes comme toi.'
                      : 'And if your coloring is ready to shine, you can share it on Le Mur, our Colomunauty gallery. Your art could inspire other colorists just like you.'}
                  </p>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {!showNoColoringMessage ? (
                  <motion.div
                    key="scanCTAs"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col gap-3"
                  >
                    <motion.button
                      onClick={handleGoToScan}
                      className="w-full py-4 rounded-full bg-[color:var(--cb-primary)] text-white font-bold shadow-[0_10px_24px_rgba(232,168,124,0.35)] flex items-center justify-center gap-2"
                      whileTap={{ scale: 0.97 }}
                    >
                      <Camera size={20} />
                      {language === 'fr' ? 'Scanner mon premier coloriage' : 'Scan my first coloring'}
                    </motion.button>

                    <motion.button
                      onClick={handleExploreWithoutScan}
                      className="w-full py-3 rounded-full bg-transparent border-2 border-[rgba(61,61,61,0.12)] text-[color:var(--cb-text-muted)] font-semibold text-sm"
                      whileTap={{ scale: 0.97 }}
                    >
                      {language === 'fr' ? "Je n'ai pas de coloriage pour l'instant" : "I don't have a coloring right now"}
                    </motion.button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="noColoringMessage"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="cozy-card p-5 text-center"
                  >
                    <div className="flex justify-center mb-3">
                      <Bongo mood="encouragement" size="medium" />
                    </div>
                    <p className="font-semibold text-[color:var(--cb-text)] mb-1">
                      {language === 'fr' ? 'Pas de souci !' : 'No worries!'}
                    </p>
                    <p className="text-sm text-[color:var(--cb-text-muted)] leading-relaxed mb-4">
                      {language === 'fr'
                        ? 'Le scan t\'attend des que tu as un coloriage sous la main. Tu peux y acceder depuis l\'accueil a tout moment.'
                        : 'The scan is waiting whenever you have a coloring ready. You can find it from the home screen anytime.'}
                    </p>
                    <motion.button
                      onClick={handleFinish}
                      className="w-full py-3 rounded-full bg-[color:var(--cb-primary)] text-white font-bold shadow-[0_10px_24px_rgba(232,168,124,0.35)] flex items-center justify-center gap-2"
                      whileTap={{ scale: 0.97 }}
                    >
                      <Sparkles size={18} />
                      {language === 'fr' ? 'Explorer Colobongo' : 'Explore Colobongo'}
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* â”€â”€ Ã‰TAPE 1 : Marques â”€â”€ */}
          {currentStepKey === STEP_BRANDS && (
            <motion.div
              key="brands"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="flex-1 flex flex-col pt-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <Paintbrush className="text-[color:var(--cb-primary)]" size={26} />
                <h2 className="text-2xl font-extrabold text-[color:var(--cb-text)]">
                  {t('whatDoYouUse')}
                </h2>
              </div>
              <p className="text-sm text-[color:var(--cb-text-muted)] mb-5">
                {t('selectBrands')}
              </p>

              <div className="flex flex-col gap-4 overflow-y-auto pb-2">
                <BrandSelector
                  language={language}
                  selectedBrands={selectedBrandIds}
                  onChange={setSelectedBrandIds}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* â”€â”€ Boutons navigation â”€â”€ */}
        {step < stepDefinitions.length - 1 && (
          <div className="pt-6 pb-[max(24px,calc(env(safe-area-inset-bottom,0px)+12px))] flex gap-3">
            {step > 0 && (
              <motion.button
                onClick={() => setStep(s => s - 1)}
                className="px-6 py-3 rounded-full bg-white border-2 border-[rgba(61,61,61,0.12)] font-semibold text-[color:var(--cb-text)]"
                whileTap={{ scale: 0.95 }}
              >
                {t('back')}
              </motion.button>
            )}
            <motion.button
              onClick={() => {
                if (currentStepKey === STEP_ARTIST) {
                  handleArtistContinue();
                  return;
                }
                setStep(s => s + 1);
              }}
              disabled={!canProceed}
              className="flex-1 py-3 rounded-full bg-[color:var(--cb-primary)] text-white font-bold shadow-[0_10px_24px_rgba(232,168,124,0.35)] disabled:opacity-50 flex items-center justify-center gap-2"
              whileTap={{ scale: 0.95 }}
              data-testid="onboarding-next-button"
            >
              {currentStepKey === STEP_ARTIST && artistSaving
                ? (language === 'fr' ? 'Sauvegarde...' : 'Saving...')
                : t('next')}
              <ChevronRight size={18} />
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
}
